import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Show, useAuth, useClerk } from "@clerk/react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { makeApi } from "@/lib/api";

const UPDATED = "June 2026";

function Page({ title, children, testid }) {
  return (
    <>
      <Nav />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid={testid}>
        <h1 className="text-4xl font-black tracking-tight">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">Last updated {UPDATED}</p>
        <div className="sticker-card p-6 sm:p-8 mt-6 space-y-4 leading-relaxed [&_h2]:text-xl [&_h2]:font-black [&_h2]:mt-6 [&_ul]:list-disc [&_ul]:pl-6">
          {children}
        </div>
      </main>
    </>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy" testid="privacy-page">
      <p>TP Quiz ("TP", "we") is a trivia and study game. This policy explains what we collect and why.</p>
      <h2>Who TP is for</h2>
      <p>TP is a general-audience app for players aged 13 and up, and it is also used by teachers with their classes. Younger children should play with a parent, guardian or teacher who sets up and supervises the account. We do not knowingly collect personal information from children under 13 without consent from a parent or school. If you believe a child has given us information without that consent, contact us and we will delete it.</p>
      <h2>What we collect</h2>
      <ul>
        <li><b>Account details:</b> your name and email, or your Google profile name and email if you sign in with Google. Sign-in is handled by our login provider, Clerk.</li>
        <li><b>Game activity:</b> quiz scores, daily streaks, badges, lives, and the classrooms you join.</li>
        <li><b>Content you create:</b> quizzes and questions you submit or build as a teacher, and teacher verification details (school name and note).</li>
        <li><b>Payments:</b> if you become a Supporter on our website, Stripe processes the payment. We never see or store your full card number.</li>
        <li><b>Ads:</b> free players can choose to watch a rewarded ad. Ads are served by Google, which may use device identifiers and cookies under Google's own policies.</li>
      </ul>
      <h2>How we use it</h2>
      <ul>
        <li>To run the game: saving scores, leaderboards, streaks, lives and classrooms.</li>
        <li>To let teachers see the scores of students in their own classrooms.</li>
        <li>To manage Supporter subscriptions and keep the app running.</li>
      </ul>
      <p>We do not sell your personal information.</p>
      <h2>Who can see your info</h2>
      <p>Your display name and scores can appear on leaderboards. Teachers can see the scores of students in their classrooms. Admins can see account details in order to moderate the app.</p>
      <h2>Keeping and deleting data</h2>
      <p>We keep your data while your account is active. You can delete your account and all of its data at any time from your Profile page, or by following the steps on our <Link className="underline font-bold" to="/delete-account">account deletion page</Link>.</p>
      <h2>Contact</h2>
      <p>Questions? Email the app owner at <a className="underline font-bold" href="mailto:dragonsong2014@gmail.com">dragonsong2014@gmail.com</a>.</p>
    </Page>
  );
}

export function Terms() {
  return (
    <Page title="Terms of Use" testid="terms-page">
      <p>By using TP Quiz you agree to these terms.</p>
      <h2>Playing fair</h2>
      <p>Be kind. Don't submit offensive, copyrighted or misleading content. We may remove content or accounts that break these rules.</p>
      <h2>Your content</h2>
      <p>You keep ownership of the quizzes you create. You give TP permission to show them to the players you share them with, or to everyone if an admin publishes them.</p>
      <h2>Supporter subscription</h2>
      <p>The Supporter plan costs $2.99 a month, is sold on our website, and renews until you cancel. You can cancel from the Plans page on the website, and the plan then ends at the close of your billing period.</p>
      <h2>No warranty</h2>
      <p>TP is provided as-is. Quiz answers are written by people and may contain mistakes.</p>
    </Page>
  );
}

function DeleteButton() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (window.prompt('This permanently deletes your account, scores, badges, classrooms and quizzes. Type DELETE to confirm.') !== "DELETE") return;
    setBusy(true);
    try {
      await api.delete("/me");
      toast.success("Your account was deleted");
      await signOut();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
      setBusy(false);
    }
  };
  return (
    <button onClick={del} disabled={busy} data-testid="delete-account-btn"
      className="px-5 py-2.5 rounded-full border-[3px] border-[#374151] bg-[#FF4B4B] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none disabled:opacity-60">
      {busy ? "Deleting…" : "Delete my account"}
    </button>
  );
}

export function DeleteAccount() {
  return (
    <Page title="Delete your TP account" testid="delete-account-page">
      <p>You can delete your TP Quiz account and its data at any time, in the Android app or on the website.</p>
      <h2>How to delete</h2>
      <ul>
        <li>Sign in to TP Quiz.</li>
        <li>Open your <b>Profile</b> (tap the flame streak counter in the top bar).</li>
        <li>Scroll down and tap <b>Delete my account</b>, then type DELETE to confirm.</li>
      </ul>
      <h2>What gets deleted</h2>
      <p>Your login, profile, scores, streaks, badges, lives, quiz submissions, teacher requests, classroom memberships, the classrooms you run, and your class-only quizzes. Any website Supporter subscription is cancelled. Quizzes that an admin already published to the public library stay up, without your name on them.</p>
      <p>Deletion happens right away. Can't sign in? Email <a className="underline font-bold" href="mailto:dragonsong2014@gmail.com">dragonsong2014@gmail.com</a> from your account's email address and we will delete it within 30 days.</p>
      <Show when="signed-in"><div className="pt-2"><DeleteButton /></div></Show>
    </Page>
  );
}

export { DeleteButton };
